
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Lock,
  Mail,
  Apple,
  Loader2,
  AlertCircle
} from "lucide-react";
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from "../../services/auth";
import { useAuth } from "../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2">
    <path
      fill="#ffffff"
      d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
    />
  </svg>
);


export default function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { setUser, checkEmailVerification } = useAuth();

  const handleLogin = async (email: string, password: string) => {
    if (isLoading) return;

    setIsLoading(true);
    setError("");

    try {
      const isVerified = await checkEmailVerification(email);

      if (!isVerified) {
        setIsLoading(false);
        navigate('/app/auth/validate-account', {
          state: {
            email: email,
            redirectTo: '/app/auth/login'
          }
        });
        return;
      }

      const response = await authService.login(email, password);
      setUser(response.user);
      return response;
    } catch (err) {
      console.error(t('login.error'), err);
      setError(err instanceof Error ? err.message : t('login.genericError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim() || !formData.password.trim()) return;

    const loginResult = await handleLogin(formData.email, formData.password);
    if (!loginResult) return;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSocialLogin = async (provider: () => Promise<any>) => {
    if (isLoading) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await provider();
      setUser(response.user);

    } catch (err) {
      console.error(t('login.error'), err);
      setError(err instanceof Error ? err.message : t('login.genericError'));
    } finally {
      setIsLoading(false);
    }
  }

  const inputClasses = "w-full bg-white/10 border border-gray-500 text-white rounded-lg py-3 px-11 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200";
  const iconClasses = "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors duration-200";
  const providerButtonClasses = "flex items-center justify-center px-4 py-2 border border-gray-500 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-3 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg flex items-center gap-2"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-4">
          <div className="relative group">
            <Mail className={`${iconClasses} group-hover:text-blue-400`} />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`${inputClasses} group-hover:border-blue-400`}
              placeholder={t('login.email')}
              required
              disabled={isLoading}
            />
          </div>

          <div className="relative group">
            <Lock className={`${iconClasses} group-hover:text-blue-400`} />
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`${inputClasses} group-hover:border-blue-400`}
              placeholder={t('login.password')}
              required
              disabled={isLoading}
            />
          </div>
        </div>

        <motion.div
          className="flex justify-end -mt-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Link
            type="button"
            to="/app/auth/forgot-password"
            className="text-sm text-blue-400 hover:text-blue-300 transition-colors duration-200"
          >
            {t('forgotPassword.title')}
          </Link>
        </motion.div>

        <motion.button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold
                   hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-blue-500/25
                   disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : null}
          {isLoading ? t('login.loading') : t('login.submit')}
        </motion.button>
      </form>

      <div className="space-y-4">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-500"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gradient-to-br from-blue-900 to-gray-900 text-gray-400">
              {t('login.orContinueWith')}
            </span>
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-xs text-gray-400 text-center"
        >
          {t('login.terms.agreement')}{' '}
          <a href="/terms" className="text-blue-400 hover:text-blue-300 transition-colors duration-200">
            {t('login.terms.termsOfService')}
          </a>{' '}
          {t('login.terms.and')}{' '}
          <a href="/privacy" className="text-blue-400 hover:text-blue-300 transition-colors duration-200">
            {t('login.terms.privacyPolicy')}
          </a>
        </motion.p>

        <div className="grid grid-cols-2 gap-3">
          <motion.button
            onClick={() => handleSocialLogin(() => authService.loginWithGoogle())}
            disabled={isLoading}
            className={providerButtonClasses}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <GoogleIcon />
            <span className="text-white">{t('login.providers.google')}</span>
          </motion.button>

          <motion.button
            onClick={() => handleSocialLogin(() => authService.loginWithApple())}
            disabled={isLoading}
            className={providerButtonClasses}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Apple className="w-5 h-5 mr-2 text-white" />
            <span className="text-white">{t('login.providers.apple')}</span>
          </motion.button>
        </div>
      </div>

      <motion.p
        className="text-center text-gray-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {t('login.noAccount')}{" "}
        <Link to="/app/auth/register">

          <motion.button
            type="button"
            disabled={isLoading}
            className="text-blue-400 hover:text-blue-300 transition-colors duration-200 font-medium"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('login.signUp')}
          </motion.button>
        </Link>
      </motion.p>
    </motion.div>
  );
}